import axios from 'axios'
import { getItemLocalStorage } from './helper.utils'
import useAuthStore from '@store/auth.store.js'
import { generateSecval } from '@utils/crypto.js'
import endpoints from '@constants/endpoints.json'
import countries from './countries'

const globalConfig = {
  retry: 3,
  retryDelay: 1000,
}

// create an axios instance
const service = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL, // url = base url + request url
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 0, // request timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language':
      getItemLocalStorage('localeCode') ||
      countries[import.meta.env.VITE_INSTITUATION]?.code ||
      'en-US',
  },
})

const organization = JSON.parse(
  JSON.stringify(endpoints[`${import.meta.env.VITE_BRAND}`]),
)

// request interceptor
service.interceptors.request.use(
  async (config) => {
    let token = getItemLocalStorage('token') || null
    if (
      !token &&
      !config.url.includes('authenticate') &&
      organization.authentication
    ) {
      const { getToken } = useAuthStore()
      token = await getToken()
    }

    if (
      organization.authentication &&
      token &&
      !config.url.includes('authenticate')
    ) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const key = `cp-${Date.now()}-${[...crypto.getRandomValues(new Uint8Array(4))].map((b) => b.toString(16).padStart(2, '0')).join('')}-${Math.random().toString(36).slice(2, 10)}`
    let secval = null
    const body = config.data
    if (organization.sendSecVal && !body.msgid) {
      secval = await generateSecval(body.respbody, key)
      config.data = { ...body, msgid: key, hmac: null, secval }
    }

    // auto select language from country configs
    config.headers = {
      ...config.headers,
      'Content-Type': 'application/json',
      'Accept-Language':
        getItemLocalStorage('localeCode') ||
        countries[import.meta.env.VITE_INSTITUATION]?.code ||
        'en-US',
    }

    return config
  },
  (error) => {
    // do something with request error
    return Promise.reject(error)
  },
)

// response interceptor
service.interceptors.response.use(
  /**
   * If you want to get http information such as headers or status
   * Please return  response => response
   */

  /**
   * Determine the request status by custom code
   * Here is just an example
   * You can also judge the status by HTTP Status Code
   */
  async (response) => {
    if (response.data?.respcode == 5 && organization.authentication) {
      const config = {
        ...response.config,
        retry: (response.config.retry ?? globalConfig.retry) - 1,
        retryDelay: response.config.retryDelay ?? globalConfig.retryDelay,
      }

      // console.log(
      //   'Retry attempt',
      //   globalConfig.retry - config.retry,
      //   'of',
      //   globalConfig.retry,
      // )

      if (config.retry < 0) {
        return Promise.reject(
          new Error(`Failed after ${globalConfig.retry} retry attempts`),
        )
      }

      try {
        localStorage.removeItem('token')
        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, config.retryDelay))

        // Parse data if needed
        if (config.data) {
          config.data = JSON.parse(config.data)
        }

        return service(config)
      } catch (error) {
        return Promise.reject(error)
      }
    }

    if (response.status >= 200 && response.status < 300) {
      return response
    }
    return null
  },
  (err) => {
    const { response, data, message, error } = err
    throw response || data || message || error || 'Error with request'
  },
)

export default service
